import {
  AGENT_APP_TOOL_REGISTRY,
  isAgentAppTool,
  type AgentAppToolDefinition,
  type AgentAppToolName,
} from './agent.js';
import type { WorkspaceRole } from './enums.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `app_op` KURAL EYLEMİ — HANGİ EKRAN İŞLEMİ KURALA BAĞLANABİLİR (Faz 7b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Karar ajanın araç sözlüğündeki BAYRAKLARDAN türüyor (`AGENT_APP_TOOL_REGISTRY`,
 * `agent.ts`) — elle yazılmış ikinci bir liste yok: sohbette ADMIN kartı olan
 * bir iş kuralda onaysız olmasın, sohbette kurala bağlanamayan bir iş (kişisel
 * veri) kuralda açılmasın.
 *
 * | Bayrak / alan                     | Kuralda                                                    |
 * |-----------------------------------|------------------------------------------------------------|
 * | okuma aracı                       | BAĞLANAMAZ — sonucu yalnız günlüğe düşerdi                 |
 * | `personalData`                    | BAĞLANAMAZ — kural gövdesinin mühür kasası yok (platform_op ile aynı) |
 * | `irreversible`                    | BAĞLANAMAZ — (aşağıda)                                     |
 * | ekip alanı (`team`)               | BAĞLANAMAZ — erişim kararı insanda (FAZ7 kararı 3)         |
 * | `needsUser` ve kredi HARCAMAYAN   | BAĞLANAMAZ — kişinin gelen kutusu/talebi/işi; kuralın "kişisi" yok |
 * | `needsUser` + `spendsCredits`     | bağlanır, KURAL SAHİBİ adına; koşu anında bakiye kontrolü  |
 * | `connection.manage` (bağlantı/mağaza) | bağlanır, HER KOŞUDA onay                              |
 * | `autopilot`                       | otomatik pilotta onaysız OLABİLİR — yön girdiye bağlı (`appOpAutopilotEligible`, API) |
 * | diğer yazmalar                    | her koşuda onay                                            |
 *
 * TAM OTONOMİDE (Faz 7c, `options.fullAutonomy` — `isFullAutonomy`):
 * `personalData`, `irreversible` ve ekip araçları BAĞLANIR; bağlanan her yazma
 * `AUTOPILOT_OK` (bağlantı/mağaza dahil — onay kuyruğuna düşmez). DEĞİŞMEYEN:
 * okuma aracı (sonucu yalnız günlüğe düşerdi — bir güvenlik kapısı değil, bir
 * anlam kapısı) ve `needsUser` + kredi harcamayan araç (işin adına yapılacağı
 * KİŞİ kuralda yok; insan tıklaması isteyen işler doğası gereği bağlanamaz).
 * Mühürlü girdi alanları (davet e-postası) eylem günlüğüne YER TUTUCU yazılır
 * (`AppOpActionService.execute`).
 *
 * ---------------------------------------------------------------------------
 * Neden "her koşuda onay" DEĞİL de "bağlanamaz" (geri alınamaz / erişim)
 * ---------------------------------------------------------------------------
 * Kural TEKRAR EDEN bir sözleşme; girdisi kurulurken sabitleniyor. Silme,
 * bağlantı kesme ya da üye çıkarma bir kez olabilir — ikinci koşunun kartı
 * zaten silinmiş bir kaydı gösteren bayat bir kart olurdu. "Her koşuda onay"
 * bunu dürüst yapmıyor, yalnız gürültü üretiyor. Erişim (ekip) ise ürün
 * sahibinin kararıyla insan işi; kural ona bir tetik koyamaz.
 *
 * ---------------------------------------------------------------------------
 * Neden kredi harcayan araç BAĞLANABİLİYOR (hepsi `needsUser`)
 * ---------------------------------------------------------------------------
 * Kredinin sahibi ve çıktının düştüğü stüdyo bir KİŞİ. Kuralda o kişi kuralı
 * yazan üye — mevcut `creative_improve` eylemiyle aynı karar
 * (`CreativeTaskRequestedEvent.requestedByUserId` = kural sahibi). Otomatik
 * pilot listesinde kredi harcayan iş yok, yani bu satırlar her koşuda kart;
 * kart doğmadan önce bakiye okunuyor ve yetmiyorsa satır `SKIPPED`
 * (`APP_OP_CREDITS_SHORT`) — onaylanamayacak bir kart açılmıyor.
 */

export type AppOpRuleBlock =
  | 'APP_OP_UNKNOWN'
  | 'APP_OP_NOT_WRITE'
  | 'APP_OP_PERSONAL_DATA'
  | 'APP_OP_IRREVERSIBLE'
  | 'APP_OP_ACCESS'
  | 'APP_OP_NEEDS_USER';

export interface AppOpRuleVerdict {
  bindable: boolean;
  /// Bağlanamıyorsa sebebi — `automation.validation.app_op_*` çevirisi bununla.
  block: AppOpRuleBlock | null;
  /**
   * `AUTOPILOT_OK` — sözlükte `autopilot: true`: otomatik pilotta onaysız
   * OLABİLİR, kesin karar girdiyle (`appOpAutopilotEligible`: kuralı
   * duraklatmak evet, açmak hayır). `ALWAYS` — her koşuda onay kuyruğu.
   */
  approval: 'AUTOPILOT_OK' | 'ALWAYS';
  /// Her koşuda onayın sebebi (arayüz künyesi).
  alwaysReason: 'CONNECTION' | 'NOT_AUTOPILOT' | null;
  spendsCredits: boolean;
  /// İş kural sahibinin ADINA yapılıyor (kredi sahibi, stüdyo).
  actsForAuthor: boolean;
  /// Kartı onaylayabilecek en düşük rol / işlemi yapabilecek en düşük rol.
  decisionRole: WorkspaceRole;
  minRole: WorkspaceRole;
}

const EMPTY: Omit<AppOpRuleVerdict, 'block'> = {
  bindable: false,
  approval: 'ALWAYS',
  alwaysReason: null,
  spendsCredits: false,
  actsForAuthor: false,
  decisionRole: 'ADMIN',
  minRole: 'ADMIN',
};

export function appOpRuleVerdict(
  tool: string,
  /** Çalışma alanında tam otonomi ETKİN mi (Faz 7c) — çağıran politikadan okur. */
  options: { fullAutonomy?: boolean } = {},
): AppOpRuleVerdict {
  if (!isAgentAppTool(tool)) return { ...EMPTY, block: 'APP_OP_UNKNOWN' };
  const definition: AgentAppToolDefinition = AGENT_APP_TOOL_REGISTRY[tool];
  const roles = { decisionRole: definition.decisionRole, minRole: definition.minRole };
  const blocked = (block: AppOpRuleBlock): AppOpRuleVerdict => ({ ...EMPTY, ...roles, block });
  const full = options.fullAutonomy === true;

  if (definition.kind !== 'write') return blocked('APP_OP_NOT_WRITE');
  /* Tam otonomide yalnız "işin kişisi yok" kapısı kalıyor (doğası gereği) —
     destek talebi hem kişisel veri hem kişiye bağlı; sebep "kişi yok". */
  if (!full) {
    if (definition.personalData) return blocked('APP_OP_PERSONAL_DATA');
    if (definition.irreversible) return blocked('APP_OP_IRREVERSIBLE');
    if (definition.domain === 'team') return blocked('APP_OP_ACCESS');
  }
  if (definition.needsUser && !definition.spendsCredits) return blocked('APP_OP_NEEDS_USER');

  const connection = definition.capability === 'connection.manage';
  const autopilot = full || (definition.autopilot && !connection);
  return {
    bindable: true,
    block: null,
    approval: autopilot ? 'AUTOPILOT_OK' : 'ALWAYS',
    alwaysReason: autopilot ? null : connection ? 'CONNECTION' : 'NOT_AUTOPILOT',
    spendsCredits: definition.spendsCredits,
    actsForAuthor: definition.needsUser,
    ...roles,
  };
}

/** Sözlükteki bütün YAZMA araçları, sözlük sırasıyla (seçici listesi). */
export function appOpWriteTools(): AgentAppToolName[] {
  return (Object.keys(AGENT_APP_TOOL_REGISTRY) as AgentAppToolName[]).filter(
    (name) => AGENT_APP_TOOL_REGISTRY[name].kind === 'write',
  );
}
